import { type ReactNode } from "react";
import { Link } from "react-router";
import {
  Menu,
  UsersIcon,
  KeyIcon,
  SettingsIcon,
  LogOutIcon,
  Music2Icon,
  FlaskConicalIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import useIsActive from "@/hooks/useIsActive";
import { useAuth } from "@/contexts/AuthContext";
import { useLogout } from "@/hooks/api/useLogout";

const menuItems = [
  {
    path: "/jams",
    title: "Jams",
    icon: Music2Icon,
  },
  {
    path: "/beta",
    title: "Beta",
    icon: FlaskConicalIcon,
  },
  {
    path: "/api-tokens",
    title: "API Tokens",
    icon: KeyIcon,
  },
  {
    path: "/settings",
    title: "Settings",
    icon: SettingsIcon,
  },
];

const adminMenuItems = [
  {
    path: "/admin/users",
    title: "Users",
    icon: UsersIcon,
  },
];

interface AdminLayoutProps {
  children: ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const { user, isAdmin } = useAuth();
  const isActive = useIsActive();
  const logout = useLogout();

  const linkStyle =
    "flex items-center gap-3 px-3 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground transition-all hover:text-foreground";
  const mobileLinkStyle =
    "mx-[-0.65rem] mt-3 flex items-center gap-4 rounded-md px-3 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground";

  const handleLogout = () => {
    logout.mutate();
  };

  return (
    <div className="grid min-h-screen w-full md:grid-cols-[220px_1fr] lg:grid-cols-[280px_1fr]">
      {/* Desktop Sidebar */}
      <div className="hidden border-r border-[#616161]/20 bg-[#191919] md:block">
        <div className="flex h-full flex-col gap-2">
          <div className="flex h-14 items-center px-4 lg:h-[60px] lg:px-6">
            <Link to="/" className="text-lg font-bold uppercase tracking-[0.2em]">
              Cobar
            </Link>
          </div>
          <div className="mt-4 flex-1">
            <nav className="grid items-start px-2 text-sm font-medium lg:px-4">
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(linkStyle, {
                    "text-primary border-l-2 border-primary": isActive(item.path),
                  })}
                >
                  <item.icon className={cn("h-4 w-4 text-muted-foreground", {
                    "text-primary": isActive(item.path),
                  })} />
                  {item.title}
                </Link>
              ))}
              {isAdmin && (
                <>
                  <div className="my-4 border-t border-[#616161]/20" />
                  <span className="px-3 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Admin
                  </span>
                  {adminMenuItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={cn(linkStyle, {
                        "text-primary border-l-2 border-primary": isActive(item.path),
                      })}
                    >
                      <item.icon className={cn("h-4 w-4 text-muted-foreground", {
                        "text-primary": isActive(item.path),
                      })} />
                      {item.title}
                    </Link>
                  ))}
                </>
              )}
            </nav>
          </div>
          <div className="sticky bottom-0 px-4 py-8">
            <div className="px-3">
              <div className="text-xs text-muted-foreground">{user?.name}</div>
              <div className="text-xs text-muted-foreground font-mono">{user?.email}</div>
              <div className="pt-3">
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground"
                >
                  <LogOutIcon className="h-4 w-4" />
                  Log out
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col">
        <header className="flex h-14 items-center gap-4 border-b border-[#616161]/20 bg-[#191919] px-4 md:border-none md:bg-transparent lg:h-[60px] lg:px-6">
          {/* Mobile Menu */}
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="shrink-0 md:hidden"
              >
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle navigation menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="flex flex-col">
              <nav className="grid gap-2 text-lg font-medium">
                <Link
                  to="/"
                  className="flex items-center gap-2 text-lg font-bold uppercase tracking-[0.2em]"
                >
                  <span>Cobar</span>
                </Link>
                {menuItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(mobileLinkStyle, {
                      "text-primary border-l-2 border-primary": isActive(item.path),
                    })}
                  >
                    <item.icon className={cn("h-5 w-5 text-muted-foreground", {
                      "text-primary": isActive(item.path),
                    })} />
                    {item.title}
                  </Link>
                ))}
                {isAdmin && (
                  <>
                    <div className="my-2 border-t border-[#616161]/20" />
                    <span className="px-3 py-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Admin
                    </span>
                    {adminMenuItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={cn(mobileLinkStyle, {
                          "text-primary border-l-2 border-primary": isActive(item.path),
                        })}
                      >
                        <item.icon className={cn("h-5 w-5 text-muted-foreground", {
                          "text-primary": isActive(item.path),
                        })} />
                        {item.title}
                      </Link>
                    ))}
                  </>
                )}
              </nav>
              <div className="mt-auto p-1">
                <div className="text-xs text-muted-foreground">{user?.name}</div>
                <div className="text-xs text-muted-foreground font-mono">
                  {user?.email}
                </div>
                <div className="pt-3">
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground hover:text-foreground"
                  >
                    <LogOutIcon className="h-5 w-5" />
                    Log out
                  </button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
          <div className="ml-auto flex gap-4"></div>
        </header>
        <main className="flex flex-1 flex-col gap-4 p-4 lg:gap-6 lg:p-6">
          <div className="max-w-5xl w-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
